import {Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges} from '@angular/core';
import {AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup} from "@angular/forms";
import {OfferingSpacesInterface} from "../model/offering-spaces.interface";
import {ManageSpacesService} from "../service/manage-spaces.service";
import {Subject, lastValueFrom, takeUntil} from "rxjs";
import {
  SpaceDefaultRate,
  RateType,
  FixedRateCategory,
  VariableRateCategory,
  TimePeriod
} from "../model/space-default-pricing.interface";
import {PanelType} from "@shared-components/info-panel/info-panel.component";

interface PeriodDef {
  timePeriod: TimePeriod;
  label: string;
}

@Component({
  selector: 'app-edit-single-space-default-pricing',
  templateUrl: './edit-single-space-default-pricing.component.html',
  styleUrls: ['./edit-single-space-default-pricing.component.scss']
})
export class EditSingleSpaceDefaultPricingComponent implements OnChanges, OnDestroy {
  @Input() selectedSpace: OfferingSpacesInterface | null = null;
  @Output('dirty') dirtyEvent = new EventEmitter<boolean>();
  @Output() spaceUpdated = new EventEmitter<OfferingSpacesInterface>();

  protected readonly PanelType = PanelType;

  private unsubscribe$ = new Subject<void>();

  dirty = false;
  defaultPricingForm!: UntypedFormGroup;

  // enums for template
  RateType = RateType;
  FixedRateCategory = FixedRateCategory;
  VariableRateCategory = VariableRateCategory;
  TimePeriod = TimePeriod;

  // periods and options
  periodDefs: PeriodDef[] = [
    { timePeriod: TimePeriod.DAILY, label: 'Daily' },
    { timePeriod: TimePeriod.WEEKLY, label: 'Weekly (7 days incl weekends)' },
    { timePeriod: TimePeriod.MONTHLY, label: 'Monthly' },
    { timePeriod: TimePeriod.WEEKEND, label: 'Weekend (Fri - Sunday)' }
  ];

  fixedRateOptions = [
    { category: FixedRateCategory.PER_SQM,    label: 'Fixed rental per sqm',   shortLabel: 'Per sqm' },
    { category: FixedRateCategory.PER_PERIOD, label: 'Fixed rental for period', shortLabel: 'Per period' }
  ];

  constructor(private spaceService: ManageSpacesService) {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedSpace']) {
      this.cancelChanges();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private initializeForm(): void {
    this.defaultPricingForm = new UntypedFormGroup({
      periods: new UntypedFormArray([])
    });

    this.periodsFA().valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.updateDirtyState());
  }

  // helpers for template
  private periodsFA(): UntypedFormArray {
    return this.defaultPricingForm.get('periods') as UntypedFormArray;
  }
  getPeriodsControls(): AbstractControl[] {
    return this.periodsFA().controls;
  }
  getRatesFA(periodIndex: number): UntypedFormArray {
    return (this.periodsFA().at(periodIndex).get('rates') as UntypedFormArray);
  }
  getRatesControls(periodIndex: number): AbstractControl[] {
    return this.getRatesFA(periodIndex).controls;
  }
  isFixed(rate: AbstractControl): boolean {
    return (rate as UntypedFormGroup).get('rateType')!.value === RateType.FIXED;
  }
  isVariable(rate: AbstractControl): boolean {
    return (rate as UntypedFormGroup).get('rateType')!.value === RateType.VARIABLE;
  }

  private buildEmptyForm(): void {
    while (this.periodsFA().length) this.periodsFA().removeAt(0);

    this.periodDefs.forEach(p => {
      const ratesFA = new UntypedFormArray([]);

      this.fixedRateOptions.forEach(opt => {
        ratesFA.push(new UntypedFormGroup({
          spaceId:    new UntypedFormControl(this.selectedSpace?.id),
          rateType:   new UntypedFormControl(RateType.FIXED),
          category:   new UntypedFormControl(opt.category),
          timePeriod: new UntypedFormControl(p.timePeriod),
          value:      new UntypedFormControl(null),
          isDefault:  new UntypedFormControl(false)
        }));
      });

      ratesFA.push(new UntypedFormGroup({
        spaceId:    new UntypedFormControl(this.selectedSpace?.id),
        rateType:   new UntypedFormControl(RateType.VARIABLE),
        category:   new UntypedFormControl(VariableRateCategory.PERCENTAGE),
        timePeriod: new UntypedFormControl(p.timePeriod),
        value:      new UntypedFormControl(null),
        isDefault:  new UntypedFormControl(false)
      }));

      this.periodsFA().push(new UntypedFormGroup({
        timePeriod: new UntypedFormControl(p.timePeriod),
        rates: ratesFA
      }));
    });
  }

  private loadSpacePricing(): void {
    if (!this.selectedSpace) {
      this.buildEmptyForm();
      this.defaultPricingForm.markAsPristine();
      this.dirty = false;
      this.dirtyEvent.emit(false);
      return;
    }

    // reset skeleton
    this.buildEmptyForm();

    const incoming = (this.selectedSpace.defaultPricing || []) as SpaceDefaultRate[];

    for (const r of incoming) {
      const periodIdx = this.periodDefs.findIndex(pd => pd.timePeriod === r.timePeriod);
      if (periodIdx < 0) continue;

      const ratesCtrls = this.getRatesControls(periodIdx);

      let match: UntypedFormGroup | undefined;
      if (r.rateType === RateType.VARIABLE) {
        match = ratesCtrls.find(ac => (ac as UntypedFormGroup).get('rateType')!.value === RateType.VARIABLE) as UntypedFormGroup | undefined;
      } else {
        match = ratesCtrls.find(ac => {
          const g = ac as UntypedFormGroup;
          return g.get('rateType')!.value === RateType.FIXED && g.get('category')!.value === r.category;
        }) as UntypedFormGroup | undefined;
      }

      if (match) {
        const num = typeof r.value === 'number' ? r.value : Number(r.value);
        match.get('value')!.setValue(Number.isFinite(num) ? num : 0);
        match.get('isDefault')!.setValue(!!r.isDefault);
      } else {
        console.warn('[PricingComponent] no matching control for rate:', r);
      }
    }


    this.defaultPricingForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });
    this.defaultPricingForm.markAsPristine();
    this.dirty = false;
    this.dirtyEvent.emit(false);
  }

  cancelChanges(): void {
    this.dirty = false;

    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.unsubscribe$ = new Subject<void>();

    this.initializeForm();
    this.loadSpacePricing();

    this.dirtyEvent.emit(false);
  }

  private updateDirtyState(): void {
    const wasDirty = this.dirty;
    this.dirty = this.defaultPricingForm.dirty;
    if (wasDirty !== this.dirty) {
      this.dirtyEvent.emit(this.dirty);
    }
  }

  get isFormValid(): boolean {
    return this.defaultPricingForm?.valid ?? false;
  }

  // UI helpers
  getPeriodLabel(periodIndex: number): string {
    const tp: TimePeriod = (this.periodsFA().at(periodIndex) as UntypedFormGroup).get('timePeriod')!.value;
    return this.periodDefs.find(p => p.timePeriod === tp)?.label ?? '';
  }

  getFixedLabel(category: FixedRateCategory): string {
    switch (category) {
      case FixedRateCategory.PER_SQM: return 'Amount (R per sqm)';
      case FixedRateCategory.PER_PERIOD: return 'Amount (R for period)';
      default: return 'Amount (R)';
    }
  }

  // fixed value input change
  onFixedValueInput(periodIndex: number, rateIndex: number): void {
    const g = (this.getRatesControls(periodIndex)[rateIndex] as UntypedFormGroup);
    const v = g.get('value')!.value;
    if (!(v > 0)) g.get('isDefault')!.setValue(false, { emitEvent: false });
  }

  // variable value input change
  onVariableValueInput(periodIndex: number): void {
    const g = this.getRatesControls(periodIndex).find(ac => (ac as UntypedFormGroup).get('rateType')!.value === RateType.VARIABLE) as UntypedFormGroup | undefined;
    if (!g) return;
    const v = g.get('value')!.value;
    if (!(v > 0)) g.get('isDefault')!.setValue(false, { emitEvent: false });
  }

  // toggle defaults
  onFixedDefaultToggle(periodIndex: number, rateIndex: number, event: any): void {
    const isSelected = event.value.includes(true);
    this.getRatesControls(periodIndex).forEach((ac, i) => {
      const g = ac as UntypedFormGroup;
      if (g.get('rateType')!.value === RateType.FIXED) {
        g.get('isDefault')!.setValue(i === rateIndex ? isSelected : false, { emitEvent: false });
      }
    });
  }

  onVariableDefaultToggle(periodIndex: number, event: any): void {
    const isSelected = event.value.includes(true);
    const g = this.getRatesControls(periodIndex).find(ac => (ac as UntypedFormGroup).get('rateType')!.value === RateType.VARIABLE) as UntypedFormGroup | undefined;
    g?.get('isDefault')!.setValue(isSelected, { emitEvent: false });
  }

  // chips summary helpers
  getSelectedFixedDefaultLabel(periodIndex: number): string | null {
    const g = this.getRatesControls(periodIndex).find(ac => {
      const fg = ac as UntypedFormGroup;
      return fg.get('rateType')!.value === RateType.FIXED && fg.get('isDefault')!.value === true;
    }) as UntypedFormGroup | undefined;

    if (!g) return null;
    const cat: FixedRateCategory = g.get('category')!.value;
    return this.fixedRateOptions.find(o => o.category === cat)?.shortLabel || '';
  }

  hasVariableDefault(periodIndex: number): boolean {
    const g = this.getRatesControls(periodIndex).find(ac => (ac as UntypedFormGroup).get('rateType')!.value === RateType.VARIABLE) as UntypedFormGroup | undefined;
    return !!g?.get('isDefault')!.value;
  }

  removeFixedDefault(periodIndex: number): void {
    this.getRatesControls(periodIndex).forEach(ac => {
      const g = ac as UntypedFormGroup;
      if (g.get('rateType')!.value === RateType.FIXED) g.get('isDefault')!.setValue(false, { emitEvent: false });
    });
    this.defaultPricingForm.markAsDirty();
    this.updateDirtyState();
  }

  removeVariableDefault(periodIndex: number): void {
    const g = this.getRatesControls(periodIndex).find(ac => (ac as UntypedFormGroup).get('rateType')!.value === RateType.VARIABLE) as UntypedFormGroup | undefined;
    g?.get('isDefault')!.setValue(false, { emitEvent: false });
    this.defaultPricingForm.markAsDirty();
    this.updateDirtyState();
  }

  async saveDefaultPricing(): Promise<void> {
    if (!this.selectedSpace || !this.isFormValid) return;

    try {
      const rates: SpaceDefaultRate[] = [];

      for (let pi = 0; pi < this.getPeriodsControls().length; pi++) {
        const ratesCtrls = this.getRatesControls(pi);
        for (let ri = 0; ri < ratesCtrls.length; ri++) {
          const g = ratesCtrls[ri] as UntypedFormGroup;
          const value = g.get('value')!.value;
          if (!(value > 0)) continue;

          rates.push({
            spaceId: this.selectedSpace.id,
            rateType: g.get('rateType')!.value,
            category: g.get('category')!.value,
            timePeriod: g.get('timePeriod')!.value,
            value: value,
            isDefault: !!g.get('isDefault')!.value
          });
        }
      }

      const updatedSpace = await lastValueFrom(this.spaceService.updateSpaceDefaultPricing(this.selectedSpace.id, rates));
      if (this.selectedSpace) this.selectedSpace.defaultPricing = updatedSpace.defaultPricing || [];

      this.loadSpacePricing();

      this.defaultPricingForm.markAsPristine();
      this.dirty = false;
      this.dirtyEvent.emit(false);

      this.spaceUpdated.emit(updatedSpace);
    } catch (error) {
      console.error('Error updating space default pricing:', error);
    }
  }
}
